import React, { useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const Profile1 = () => {
  // --- TAB STATE --- //
  const [activeTab, setActiveTab] = useState('Profile');

  // --- MOCK DATA: MAIN HEADER --- //
  const metricsData = [
    { title: 'TOTAL BIDS', value: '67', subtext: 'Since May 2024' },
    { title: 'TENDERS WON', value: '28', subtext: 'L1 awards', trend: '▲ Above avg', trendColor: '#16a34a' },
    { title: 'TENDERS LOST', value: '34', subtext: 'Incl. disqualified' },
    { title: 'WIN RATE', value: '41.8%', subtext: 'Avg: 29.4%', trend: '▲ Above avg', trendColor: '#16a34a' },
    { title: 'TOTAL BID VALUE', value: '₹9.2 Cr', subtext: 'Cumulative' },
    { title: 'REVENUE', value: '₹84,000', subtext: 'Renews: May 2025' },
  ];

  // --- MOCK DATA: PROFILE TAB --- //
  const companyInfoLeft = [
    { label: 'Document Name', value: 'Semiconductor Ltd' },
    { label: 'Primary Contact', value: 'Deepa Nair' },
    { label: 'Email ID', value: 'deepa@semicond.in' },
    { label: 'Phone', value: '+91 85748 39402' },
    { label: 'City', value: 'Bangalore' },
    { label: 'State', value: 'Karnataka' },
    { label: 'GST Number', value: '29AKEDN123KSN2' },
    { label: 'Category', value: 'Electronics' },
  ];

  const companyInfoRight = [
    { label: 'Subscription Plan', value: 'Premium' },
    { label: 'Member Since', value: 'May 2024' },
    { label: 'Renewal Date', value: 'May 2026' },
    { label: 'Total Revenue', value: '₹1.38Cr' },
  ];

  const categories = [
    { name: 'Electronics', status: 'Active' },
    { name: 'IT Service', status: 'Active' },
    { name: 'Defence', status: 'Active' },
  ];

  const stateCoverage = [
    { name: 'Karnataka', type: 'Primary' },
    { name: 'Tamil Nadu', type: 'Additional' },
    { name: 'Kerela', type: 'Additional' },
  ];

  // --- MOCK DATA: PERFORMANCE TAB --- //
  const performanceMetrics = [
    { title: 'WIN RATE', value: '41.8%', subtext: 'vs 29.4% avg', trend: '▲ 7.2% above avg', trendColor: '#16a34a' },
    { title: 'TOTAL BIDS', value: '76', subtext: 'All Time' },
    { title: 'L1 AWARDS', value: '24', subtext: 'First Rank Wins' },
    { title: 'AVG BID VALUE', value: '₹1.32Cr', subtext: 'Per Tender', trend: '▲ 12% above platform', trendColor: '#16a34a' },
  ];

  const winRateData = [
    { month: 'Jan', value: 2.3, display: '9%' },
    { month: 'Feb', value: 3.0, display: '12%' },
    { month: 'Mar', value: 2.8, display: '11%' },
    { month: 'Apr', value: 3.8, display: '15%' },
    { month: 'May', value: 3.6, display: '14%' },
    { month: 'Jun', value: 4.3, display: '17%' },
    { month: 'Jul', value: 5.8, display: '23%' }, // Matches design tooltip exactly
    { month: 'Aug', value: 4.0, display: '16%' },
    { month: 'Sep', value: 5.0, display: '20%' },
    { month: 'Oct', value: 6.8, display: '27%' },
  ];

  const platformAverages = [
    { label: 'Win Rate', value: 42, text: '42% vs 28.3%' },
    { label: 'Avg Bid Value', value: 52, text: '52% vs 38.3%' },
    { label: 'L1 Count', value: 68, text: '68% vs 40. %' },
    { label: 'Response Rate', value: 88, text: '88% vs 70.6%' },
  ];

  // --- HELPER COMPONENTS & STYLES --- //
  const cardStyle = { border: '1px solid #e5e7eb', borderRadius: '10px', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' };
  
  const ActiveBadge = () => (
    <span className="badge rounded-pill" style={{ backgroundColor: '#dcfce7', color: '#166534', padding: '0.35rem 0.75rem', fontWeight: '600', fontSize: '0.7rem' }}>Active</span>
  );
  
  const PremiumBadge = () => (
    <span className="badge rounded-pill" style={{ backgroundColor: '#f3e8ff', color: '#7e22ce', padding: '0.35rem 0.75rem', fontWeight: '600', fontSize: '0.7rem' }}>Premium</span>
  );

  const InfoRow = ({ label, value }) => (
    <div style={{ display: 'grid', gridTemplateColumns: '130px 15px 1fr', alignItems: 'center', marginBottom: '0.85rem', fontSize: '0.85rem' }}>
      <div style={{ color: '#6366f1', fontWeight: '500' }}>{label}</div>
      <div style={{ color: '#9ca3af', textAlign: 'center' }}>:</div>
      <div style={{ color: '#111827', fontWeight: '500' }}>{value}</div>
    </div>
  );

  const CustomChartTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-2 border shadow-sm rounded" style={{ fontSize: '0.75rem', color: '#111827' }}>
          <div className="text-muted mb-1">{label}</div>
          <div style={{ color: '#0f766e', fontWeight: '600' }}>Win Rate: {payload[0].payload.display}</div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="container-fluid py-3" style={{ backgroundColor: '#f9fafb', minHeight: '100vh', fontFamily: 'Inter, system-ui, sans-serif' }}>
      
      {/* Top Header Section */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center gap-3">
          <div className="rounded-circle d-flex justify-content-center align-items-center text-white shadow-sm" style={{ width: '42px', height: '42px', backgroundColor: '#0f766e', fontSize: '1.1rem', fontWeight: '600' }}>
            SL
          </div>
          <div>
            <h5 className="mb-0 fw-bold" style={{ color: '#111827' }}>Semiconductors Ltd</h5>
            <div className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>
              deepa@semicond.in • +91 9111222333 • Joined May 2024
            </div>
          </div>
        </div>
        <div className="d-flex gap-2">
          <ActiveBadge />
          <PremiumBadge />
        </div>
      </div>

      {/* Main Summary Metrics Cards */}
      <div className="row g-2 mb-3">
        {metricsData.map((card, idx) => (
          <div className="col" key={idx} style={{ minWidth: '16%' }}>
            <div className="card h-100 bg-white p-3" style={cardStyle}>
              <h6 className="mb-2 text-uppercase" style={{ fontSize: '0.65rem', fontWeight: '700', color: '#6b7280', letterSpacing: '0.05em' }}>{card.title}</h6>
              <h4 className="mb-1 fw-bold" style={{ color: '#111827', fontSize: '1.4rem' }}>{card.value}</h4>
              <p className="mb-1" style={{ fontSize: '0.7rem', color: '#6b7280' }}>{card.subtext}</p>
              {card.trend && (
                <p className="mb-0" style={{ fontSize: '0.65rem', color: card.trendColor, fontWeight: '600' }}>{card.trend}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Dynamic Tabs */}
      <div className="d-flex gap-4 mb-3" style={{ borderBottom: '1px solid #e5e7eb' }}>
        {['Profile', 'Performance'].map((tab) => (
          <div
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              paddingBottom: '8px',
              borderBottom: activeTab === tab ? '2px solid #0f766e' : '2px solid transparent',
              color: activeTab === tab ? '#0f766e' : '#6b7280',
              fontWeight: activeTab === tab ? '600' : '500',
              fontSize: '0.85rem',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out'
            }}
          >
            {tab}
          </div>
        ))}
      </div>

      {/* ========================================= */}
      {/* PROFILE TAB                  */}
      {/* ========================================= */}
      {activeTab === 'Profile' && (
        <div className="row g-3">
          {/* LEFT COLUMN: Company Information */}
          <div className="col-lg-8">
            <div className="card bg-white p-4 h-100" style={cardStyle}>
              <h6 className="fw-bold mb-4" style={{ color: '#111827' }}>Company Information</h6>
              <div className="row">
                <div className="col-md-7">
                  {companyInfoLeft.map((info, idx) => (
                    <InfoRow key={idx} label={info.label} value={info.value} />
                  ))}
                </div>
                <div className="col-md-5">
                  {companyInfoRight.map((info, idx) => (
                    <InfoRow key={idx} label={info.label} value={info.value} />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: Status & Coverage */}
          <div className="col-lg-4 d-flex flex-column gap-3">
            {/* Account Status Card */}
            <div className="card bg-white p-3" style={cardStyle}>
              <h6 className="fw-bold mb-3" style={{ color: '#111827', fontSize: '0.9rem' }}>Account Status</h6>
              <div className="d-flex gap-3">
                <div className="flex-fill rounded p-2 d-flex align-items-center justify-content-between gap-2" style={{ backgroundColor: '#f9fafb', border: '1px solid #f3f4f6' }}>
                  <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>Account Status :</span>
                  <ActiveBadge />
                </div>
                <div className="flex-fill rounded p-2 d-flex align-items-center justify-content-between gap-2" style={{ backgroundColor: '#f9fafb', border: '1px solid #f3f4f6' }}>
                  <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>Plan Tier :</span>
                  <PremiumBadge />
                </div>
              </div>
            </div>

            {/* Tender Categories Active Card */}
            <div className="card bg-white p-3" style={cardStyle}>
              <h6 className="fw-bold mb-3" style={{ color: '#111827', fontSize: '0.9rem' }}>Tender Categories Active</h6>
              <div className="d-flex flex-column gap-2">
                {categories.map((cat, idx) => (
                  <div key={idx} className="d-flex justify-content-between align-items-center">
                    <span style={{ color: '#6366f1', fontSize: '0.85rem', fontWeight: '500' }}>{cat.name}</span>
                    <ActiveBadge />
                  </div>
                ))}
              </div>
            </div>

            {/* State Coverage Card */}
            <div className="card bg-white p-3 flex-grow-1" style={cardStyle}>
              <h6 className="fw-bold mb-3" style={{ color: '#111827', fontSize: '0.9rem' }}>State Coverage</h6>
              <div className="d-flex flex-column gap-2">
                {stateCoverage.map((state, idx) => (
                  <div key={idx} className="d-flex justify-content-between align-items-center">
                    <span style={{ color: '#111827', fontSize: '0.85rem', fontWeight: '500' }}>{state.name}</span>
                    <span style={{ color: '#6b7280', fontSize: '0.8rem', fontWeight: '500' }}>{state.type}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================= */}
      {/* PERFORMANCE TAB                */}
      {/* ========================================= */}
      {activeTab === 'Performance' && (
        <>
          {/* Sub-Metrics Cards */}
          <div className="row g-3 mb-3">
            {performanceMetrics.map((card, idx) => (
              <div className="col" key={idx}>
                <div className="card h-100 bg-white p-3" style={cardStyle}>
                  <h6 className="mb-2 text-uppercase" style={{ fontSize: '0.65rem', fontWeight: '700', color: '#6b7280', letterSpacing: '0.05em' }}>{card.title}</h6>
                  <h4 className="mb-3 fw-bold" style={{ color: '#111827', fontSize: '1.4rem' }}>{card.value}</h4>
                  <div className="d-flex justify-content-between align-items-end mt-auto">
                    <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>{card.subtext}</span>
                    {card.trend && (
                      <span style={{ fontSize: '0.7rem', color: card.trendColor, fontWeight: '600' }}>{card.trend}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Charts Row */}
          <div className="row g-3">
            
            {/* LEFT COLUMN: Line Chart */}
            <div className="col-lg-8">
              <div className="card bg-white p-4 h-100" style={cardStyle}>
                <h6 className="fw-bold mb-4" style={{ color: '#111827', fontSize: '0.9rem' }}>Monthly Win Rate Trend</h6>
                <div style={{ height: '220px', width: '100%' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={winRateData} margin={{ top: 10, right: 10, left: -25, bottom: 0 }}>
                      <defs>
                        <linearGradient id="colorWinRate" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#0f766e" stopOpacity={0.15}/>
                          <stop offset="95%" stopColor="#0f766e" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e5e7eb" />
                      <XAxis dataKey="month" axisLine={true} tickLine={false} tick={{ fontSize: 11, fill: '#6b7280' }} dy={10} />
                      <YAxis domain={[0, 8]} ticks={[0, 2, 4, 6, 8]} axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#6b7280' }} />
                      <Tooltip content={<CustomChartTooltip />} cursor={{ stroke: '#e5e7eb', strokeWidth: 1, strokeDasharray: '3 3' }} />
                      <Area 
                        type="monotone" 
                        dataKey="value" 
                        stroke="#0f766e" 
                        strokeWidth={2} 
                        fillOpacity={1} 
                        fill="url(#colorWinRate)" 
                        activeDot={{ r: 6, fill: '#0f766e', stroke: '#fff', strokeWidth: 2 }}
                        dot={{ r: 4, fill: '#0f766e', strokeWidth: 0 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN: Horizontal Progress Bars */}
            <div className="col-lg-4">
              <div className="card bg-white p-4 h-100" style={cardStyle}>
                <h6 className="fw-bold mb-4" style={{ color: '#111827', fontSize: '0.9rem' }}>Performance vs Platform Average</h6>
                
                <div className="d-flex flex-column justify-content-between h-100 pb-2">
                  {platformAverages.map((item, idx) => (
                    <div key={idx} className="mb-3">
                      <div className="d-flex justify-content-between align-items-end mb-2">
                        <span style={{ fontSize: '0.8rem', color: '#111827', fontWeight: '500' }}>{item.label}</span>
                        <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>{item.text}</span>
                      </div>
                      <div style={{ height: '8px', backgroundColor: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{ width: `${item.value}%`, height: '100%', backgroundColor: '#0f766e', borderRadius: '4px' }}></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </>
      )}

    </div>
  );
};

export default Profile1;